import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogContent } from "@mui/material";
import { getAuth } from "firebase/auth";
// import admin from "firebase-admin";
import { X, Users, Factory, ChevronDown, PencilRuler, ChevronLeft, ChevronRight } from "lucide-react";
import CompanyActivityBar from "./CompanyActivityBar";
import TaplrTooltip from "../../../Shared/Tooltip";

export default function CreateAccountDialog({ dialogOpen, closeDialog, subscriberToBill, ...props }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const auth = getAuth();

  const [generalError, setGeneralError] = useState(null);
  const [disclaimer, setDisclaimer] = useState(null);

  const [resetStartDate, setResetStartDate] = useState(false);
  // general
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // collabsibles
  const [companyCollapsed, setCompanyCollapsed] = useState(true);
  const [userCollapsed, setUserCollapsed] = useState(true);
  const [projectCollapsed, setProjectCollapsed] = useState(true);

  // data
  const [subscriberUsage, setSubscriberUsage] = useState(null);
  const [allSubscriptionTiers, setAllSubscriptionTiers] = useState([]);

  const resetFields = () => {
    setDisclaimer(null);
    setGeneralError(null);
    setCompanyCollapsed(true);
    setUserCollapsed(true);
    setProjectCollapsed(true);
    setStartDate(null);
    setEndDate(null);
    setSubscriberUsage(null);
    setLoading(true);
  };

  const handleCloseDialog = () => {
    resetFields();
    closeDialog();
  };

  const adjustToUTC = (date) => {
    // Adjust the local Brussels time to UTC by removing the timezone offset
    const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return utcDate.toISOString(); // Convert to ISO format
  };

  useEffect(() => {
    // get for this month 1st to last day of the month
    if (subscriberToBill) {
      // fromDate is the first of this month (utc)
      const fromDate = adjustToUTC(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
      // toDate is the last day of this month (utc)
      const toDate = adjustToUTC(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59));
      setStartDate(fromDate);
      setEndDate(toDate);
      dataService
        .getSubscriberUsageByUniqueIdForDateRange(subscriberToBill.uniqueId, fromDate, toDate)
        .then((response) => {
          if (response && response.success) {
            setSubscriberUsage(response.subscriberUsage);
          } else if (response && !response.success) {
            setGeneralError(response.message);
          }
        })
        .catch((error) => {
          if (error.status === 403) {
            setGeneralError(t('errors.unauthorized'));
          } else {
            setGeneralError(t('subscriberBillingDialog.fetchUsageError'));
          }
        })
        .then(() => {
          setLoading(false);
        });
    }
  }, [subscriberToBill]);

  const nextUsagePeriod = () => {
    const currentStartDate = new Date(startDate);

    // Start of next month (UTC)
    const nextStartDate = new Date(Date.UTC(currentStartDate.getUTCFullYear(), currentStartDate.getUTCMonth() + 1, 1, 0, 0, 0));

    // End of next month (UTC)
    const nextEndDate = new Date(Date.UTC(currentStartDate.getUTCFullYear(), currentStartDate.getUTCMonth() + 2, 0, 23, 59, 59));

    console.log(nextStartDate.toISOString(), nextEndDate.toISOString());

    getCompanyUsageData(nextStartDate, nextEndDate);
  };

  const previousUsagePeriod = () => {
    const currentStartDate = new Date(startDate);

    // Start of previous month (UTC)
    const prevStartDate = new Date(Date.UTC(currentStartDate.getUTCFullYear(), currentStartDate.getUTCMonth() - 1, 1, 0, 0, 0));

    // End of previous month (UTC)
    const prevEndDate = new Date(Date.UTC(currentStartDate.getUTCFullYear(), currentStartDate.getUTCMonth(), 0, 23, 59, 59));

    console.log(prevStartDate.toISOString(), prevEndDate.toISOString());

    getCompanyUsageData(prevStartDate, prevEndDate);
  };

  const getCompanyUsageData = (fromDate, toDate) => {
    setLoading(true);
    setGeneralError(null);
    setStartDate(fromDate);
    setEndDate(toDate);
    dataService
      .getSubscriberUsageByUniqueIdForDateRange(subscriberToBill.uniqueId, adjustToUTC(fromDate), adjustToUTC(toDate))
      .then((response) => {
        if (response && response.success) {
          setSubscriberUsage(response.subscriberUsage);
        } else if (response && !response.success) {
          setGeneralError(response.message);
        }
      })
      .catch((error) => {
        if (error.status === 403) {
          setGeneralError(t('errors.unauthorized'));
        } else {
          setGeneralError(t('subscriberBillingDialog.fetchUsageError'));
        }
      })
      .then(() => {
        setLoading(false);
      });
  };

  const handleToggleSelection = (state) => {
    setGeneralError(null);
    setSelectedTab(state);
  };

  // function to get the number of days active for a company
  const getCompanyDaysActive = (activeDateRanges) => {
    let totalDays = 0;
    if (activeDateRanges && activeDateRanges.length > 0) {
      activeDateRanges.forEach((dateRange) => {
        const startDate = new Date(dateRange.startDate);
        const endDate = new Date(dateRange.endDate);
        const diffTime = Math.abs(endDate - startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        totalDays += diffDays;
      });
    }
    return totalDays;
  };

  const getCurrentUsagePeriod = () => {
    // return month - year of the current dateRange in English
    let currentStartDate = new Date(startDate);

    let month = currentStartDate.toLocaleString("en-US", { month: "long" });
    let year = currentStartDate.getFullYear();
    return `${month} ${year}`;
  };

  const toggleCompanyCollapsed = () => setCompanyCollapsed((prev) => !prev);
  const toggleUserCollapsed = () => setUserCollapsed((prev) => !prev);
  const toggleProjectCollapsed = () => setProjectCollapsed((prev) => !prev);

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <h1 className={classes.dialog__title}>{subscriberToBill && subscriberToBill.name} - {t('subscriberBillingDialog.billing')}</h1>
        <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
          <X size={19} />
        </button>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          <div className={classes.dateSelectorContainer}>
            <button className={classes.dateSelector__button} onClick={() => previousUsagePeriod()}>
              <ChevronLeft size={16} strokeWidth={1.4} color="#0C0E29" />
            </button>
            <p className={classes.dateSelector__label}>{getCurrentUsagePeriod()}</p>
            <button className={classes.dateSelector__button} onClick={() => nextUsagePeriod()}>
              <ChevronRight size={16} strokeWidth={1.4} color="#0C0E29" />
            </button>
          </div>
          <div className={classes.toggleFilter}>
            <button onClick={() => handleToggleSelection(0)} style={{ width: "50%" }} className={classes.toggleFilter__optionButton}>
              <p className={classes.toggleFilter__label}>{t('subscriberBillingDialog.usage')}</p>
            </button>
            <button onClick={() => handleToggleSelection(1)} style={{ width: "50%" }} className={classes.toggleFilter__optionButton}>
              <p className={classes.toggleFilter__label}>{t('subscriberBillingDialog.bill')}</p>
            </button>
            <div style={{ transform: selectedTab == 0 ? "translateX(0px)" : "translateX(100%)" }} className={classes.toggleSelected} />
          </div>
          {generalError && (
            <div className={classes.errorContainer}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          {subscriberUsage && (
            <div className={classes.usageContainer}>
              <div className={classes.usageContainer__usageCategory}>
                <div className={classes.usageContainer__categoryHeader} onClick={toggleCompanyCollapsed}>
                  <div className={classes.usageContainer__categoryTitleContainer}>
                    <Factory size={20} strokeWidth={1.4} color="#0C0E29" />
                    <p className={classes.usageContainer__categoryTitle}>{t('subscriberOverview.companies')}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p className={classes.usageContainer__categoryCount}>{t('subscriberBillingDialog.max')}: {subscriberUsage.companyUsage.maxSimultaneouslyActiveCompanies}</p>
                    <button className={classes.usageContainer__toggleButton}>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.4}
                        color="#0C0E29"
                        style={{
                          transform: companyCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </button>
                  </div>
                </div>
                {!companyCollapsed && (
                  <div className={classes.usageContainer__categoryUsage}>
                    <div className={classes.dividerLine} />
                    {subscriberUsage.companyUsage.maxSimultaneouslyActiveCompanies > 0 && (
                      <div className={classes.usageContainer__companyUsage}>
                        {subscriberUsage.companyUsage.companies
                          .filter((company) => company.activeDateRanges.length > 0) // Filter companies with non-empty activeDateRanges
                          .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name
                          .map((company) => {
                            return (
                              <div key={company.name} className={classes.companyUsage__companyContainer}>
                                <div className={classes.companyUsage__companyHeader}>
                                  <p className={classes.companyUsage__companyName}>{company.name}</p>
                                  <p className={classes.companyUsage__companyDaysCount}>{getCompanyDaysActive(company.activeDateRanges)} {t('subscriberBillingDialog.days')}</p>
                                </div>
                                <CompanyActivityBar activeRanges={company.activeDateRanges} targetMonth={new Date(startDate)} />
                                {/* bar that represents start */}
                              </div>
                            );
                          })}
                      </div>
                    )}
                    {subscriberUsage.companyUsage.maxSimultaneouslyActiveCompanies === 0 && (
                      <div className={classes.usageContainer__emptyStateContainer}>
                        <p>{t('subscriberBillingDialog.noActiveCompanies', { period: getCurrentUsagePeriod() })}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className={classes.usageContainer__usageCategory}>
                <div className={classes.usageContainer__categoryHeader} onClick={toggleProjectCollapsed}>
                  <div className={classes.usageContainer__categoryTitleContainer}>
                    <PencilRuler size={20} strokeWidth={1.4} color="#0C0E29" />
                    <p className={classes.usageContainer__categoryTitle}>{t('project.projects')}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p className={classes.usageContainer__categoryCount}>{t('subscriberBillingDialog.max')}: {subscriberUsage.projectUsage.maxSimultaneouslyActiveProjects}</p>
                    <button className={classes.usageContainer__toggleButton}>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.4}
                        color="#0C0E29"
                        style={{
                          transform: projectCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </button>
                  </div>
                </div>
                {!projectCollapsed && (
                  <div className={classes.usageContainer__categoryUsage}>
                    <div className={classes.dividerLine} />
                    {subscriberUsage.projectUsage.maxSimultaneouslyActiveProjects > 0 && (
                      <div className={classes.usageContainer__companyUsage}>
                        {subscriberUsage.projectUsage.projects
                          .filter((project) => project.activeDateRanges.length > 0)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((project) => {
                            return (
                              <div key={project.name} className={classes.companyUsage__companyContainer}>
                                <div className={classes.companyUsage__companyHeader}>
                                  <p className={classes.companyUsage__companyName}>{project.name}</p>
                                  <p className={classes.companyUsage__companyDaysCount}>{getCompanyDaysActive(project.activeDateRanges)} {t('subscriberBillingDialog.days')}</p>
                                </div>
                                <CompanyActivityBar activeRanges={project.activeDateRanges} targetMonth={new Date(startDate)} />
                                {/* bar that represents start */}
                              </div>
                            );
                          })}
                      </div>
                    )}
                    {subscriberUsage.projectUsage.maxSimultaneouslyActiveProjects === 0 && (
                      <div className={classes.usageContainer__emptyStateContainer}>
                        <p>{t('subscriberBillingDialog.noActiveProjects', { period: getCurrentUsagePeriod() })}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className={classes.usageContainer__usageCategory}>
                <div className={classes.usageContainer__categoryHeader} onClick={toggleUserCollapsed}>
                  <div className={classes.usageContainer__categoryTitleContainer}>
                    <Users size={20} strokeWidth={1.4} color="#0C0E29" />
                    <p className={classes.usageContainer__categoryTitle}>{t('subscriberOverview.users')}</p>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <p className={classes.usageContainer__categoryCount}>{t('subscriberBillingDialog.current')}: {subscriberUsage.userCount}</p>
                    <button className={classes.usageContainer__toggleButton}>
                      <ChevronDown
                        size={16}
                        strokeWidth={1.4}
                        color="#0C0E29"
                        style={{
                          transform: userCollapsed ? "rotate(0deg)" : "rotate(-180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </button>
                  </div>
                </div>
                {!userCollapsed && (
                  <div className={classes.usageContainer__categoryUsage}>
                    <div className={classes.dividerLine} />
                    <div className={classes.usageContainer__companyUsage}>
                      <p>{t('subscriberBillingDialog.comingSoon')}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {disclaimer && (
            <div className={classes.disclaimerContainer}>
              <p className={classes.disclaimerMessage}>{disclaimer}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "480px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "16px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    padding: "0px !important",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  dateSelectorContainer: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",

    backgroundColor: "white",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",

    padding: "1px",
    marginBottom: "8px",
  },

  dateSelector__button: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0px",
    margin: "0px",
    height: "28px",
    width: "28px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    transition: "all ease-in-out 0.2s",
    color: "#0C0E29",
    "&:hover": {
      backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dateSelector__label: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    fontWeight: "normal",
  },

  toggleFilter: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",
    marginBottom: "16px",
  },

  toggleFilter__optionButton: {
    height: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(50% - 1px)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  usageContainer: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  usageContainer__usageCategory: {
    width: "100%",

    padding: "16px",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
  },

  usageContainer__categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  usageContainer__categoryTitleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  usageContainer__categoryTitle: {
    height: "24px",
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  usageContainer__categoryCount: {
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  usageContainer__toggleButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "0px",
    margin: "0px",
    height: "24px",
    width: "24px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "4px",
    transition: "all ease-in-out 0.2s",
    "&:hover": {
      backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  usageContainer__emptyStateContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  usageContainer__companyUsage: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  companyUsage__companyContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },

  companyUsage__companyHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },

  companyUsage__companyName: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  companyUsage__companyDaysCount: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  usageContainer__categoryUsage: {},

  dividerLine: {
    width: "100%",
    height: "1px",
    backgroundColor: "#DCDCDC",
    marginBottom: "1rem",
    marginTop: "1rem",
    borderRadius: "1px",
  },

  usageContainer__categoryUsageLabel: {},

  usageContainer__categoryUsageValue: {},

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  disclaimerContainer: {
    width: "100%",
    backgroundColor: "#F8F8F8",

    border: "1px solid #EDEDED",
    borderRadius: "6px",

    marginTop: "16px",
    padding: "12px",
  },

  disclaimerMessage: {
    fontSize: "16px",
    color: "#0C0E29",
  },
}));
